import { makeAutoObservable, runInAction } from 'mobx';
import professionalModel from "../../../model/professional/professional-model";
import companyStore from "../../../store/company-store";

// Types
export interface Permission {
  id: string;
  name: string;
}

export interface Role {
  id: string;
  name: string;
}

export interface Company {
  id: string;
  businessName: string;
}

export interface CollaboratorFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  companyId: string;
  roleId: string;
  permissionIds: string[];
}

export class CompanyAddCollaboratorModalViewModel {

  formData: CollaboratorFormData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    companyId: '',
    roleId: '',
    permissionIds: []
  };

  errors: Partial<Record<keyof CollaboratorFormData, string>> = {};
  isSubmitting: boolean = false;
  showPermissions: boolean = false;
  isLoading: boolean = false;
  error: string | null = null;

  availableRoles: Role[] = [];
  availablePermissions: Permission[] = [];
  availableCompanies: Company[] = [];

  private static instance: CompanyAddCollaboratorModalViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyAddCollaboratorModalViewModel {
    if (!CompanyAddCollaboratorModalViewModel.instance) {
      CompanyAddCollaboratorModalViewModel.instance = new CompanyAddCollaboratorModalViewModel();
    }
    return CompanyAddCollaboratorModalViewModel.instance;
  }

  setFormData(data: Partial<CollaboratorFormData>) {
    this.formData = { ...this.formData, ...data };
  }

  setErrors(errors: Partial<Record<keyof CollaboratorFormData, string>>) {
    this.errors = errors;
  }

  clearError(field: keyof CollaboratorFormData) {
    const newErrors = { ...this.errors };
    delete newErrors[field];
    this.errors = newErrors;
  }

  setIsSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  setShowPermissions(show: boolean) {
    this.showPermissions = show;
  }

  setAvailableRoles(roles: Role[]) {
    this.availableRoles = roles;
  }

  setAvailablePermissions(permissions: Permission[]) {
    this.availablePermissions = permissions;
  }

  get groupedPermissions(): Record<string, Permission[]> {
    const groupedPermissions: Record<string, Permission[]> = {};

    this.availablePermissions.forEach(permission => {
      const parts = permission.name.split(' ');
      if (parts.length > 0) {
        const action = parts[0];

        if (!groupedPermissions[action]) {
          groupedPermissions[action] = [];
        }
        groupedPermissions[action].push(permission);
      }
    });

    return groupedPermissions;
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    this.setFormData({ [name]: value });

    if (this.errors[name as keyof CollaboratorFormData]) {
      this.clearError(name as keyof CollaboratorFormData);
    }
  };

  isPermissionSelected = (permissionId: string): boolean => {
    return this.formData.permissionIds.includes(permissionId);
  };

  togglePermission = (permissionId: string) => {
    if (this.isPermissionSelected(permissionId)) {
      this.setFormData({
        permissionIds: this.formData.permissionIds.filter(id => id !== permissionId)
      });
    } else {
      this.setFormData({
        permissionIds: [...this.formData.permissionIds, permissionId]
      });
    }
  };

  togglePermissionGroup = (_groupName: string, permissions: Permission[]) => {
    const permissionIds = permissions.map(p => p.id);
    const allSelected = permissions.every(p => this.isPermissionSelected(p.id));

    if (allSelected) {
      this.setFormData({
        permissionIds: this.formData.permissionIds.filter(id => !permissionIds.includes(id))
      });
    } else {
      const newIds = permissionIds.filter(id => !this.formData.permissionIds.includes(id));
      this.setFormData({
        permissionIds: [...this.formData.permissionIds, ...newIds]
      });
    }
  };

  // Méthode pour charger les rôles, permissions et entreprises depuis l'API
  async initialize() {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await professionalModel.handleGetDataRolesAndPermissions();

      runInAction(() => {
        this.availableRoles = response.roles;
        this.availablePermissions = response.permissions;

        this.availableCompanies = companyStore.companies.map(company => ({
          id: company.id,
          businessName: company.businessName
        }));

        if (!this.formData.roleId && response.roles.length > 0) {
          this.formData.roleId = response.roles[0].id;
        }

        if (!this.formData.companyId && this.availableCompanies.length > 0) {
          this.formData.companyId = this.availableCompanies[0].id;
        }

        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = "Erreur lors du chargement des rôles et permissions";
        this.isLoading = false;
      });
      console.error("Erreur API initialize:", error);
    }
  }

  validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CollaboratorFormData, string>> = {};

    if (!this.formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }

    if (!this.formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    if (!this.formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^\S+@\S+\.\S+$/.test(this.formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!this.formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (this.formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (this.formData.password !== this.formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!this.formData.companyId) {
      newErrors.companyId = 'Veuillez sélectionner une entreprise';
    }

    if (!this.formData.roleId) {
      newErrors.roleId = 'Veuillez sélectionner un rôle';
    }

    this.setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent, onAddCollaborator: (data: CollaboratorFormData) => Promise<any>, onClose: () => void) => {
    e.preventDefault();

    if (this.validateForm()) {
      this.setIsSubmitting(true);
      try {
        await onAddCollaborator(this.formData);
      } catch (error) {
        console.error('Erreur lors de la soumission:', error);
        this.setIsSubmitting(false);
      }
      // Le finally ne s'exécutera pas car window.location.reload() interrompt l'exécution
    }
  };

  resetForm = () => {
    this.formData = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      companyId: this.availableCompanies[0]?.id || '',
      roleId: this.availableRoles[0]?.id || '',
      permissionIds: []
    };
    this.errors = {};
    this.showPermissions = false;
  };
}

export default CompanyAddCollaboratorModalViewModel.getInstance();
